<template>
  <v-container data-testid="home" fluid v-bind:class="{'pt-0':currentUser.clientType === 'Media'}" >
    <v-row>
      <v-col cols="12" class="pb-0 px-2">
        <!--Header-->
        <div class="mb-2">
          <h2 data-testid="home-header" class="text-uppercase font-weight-bold" style="font-size: 16px;">{{`${currentUser.clientName} · ${$t('homeStats.data')}`}}</h2>
        </div>

        <!--Stats Cards - Desktop/Tablet Landscape Layout-->
        <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex mb-2" style="gap: 11px; width: 100%; flex-wrap: wrap;">
          <!--Players Card-->
          <div style="flex: 1 1 0; min-width: 0;">
            <v-card data-testid="home-card-players" elevation="0" outlined class="stats-card d-flex flex-column" style="border-radius: 8px; height: 100%;">
              <div v-if="loading || !driblabStats" class="pa-4 d-flex flex-column" style="height: 100%;">
                <div class="d-flex justify-space-between align-start mb-4">
                  <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                  <v-skeleton-loader type="avatar" size="28" class="ma-0"></v-skeleton-loader>
                </div>
                <div class="mb-4" style="flex-grow: 1;">
                  <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
                </div>
                <div class="d-flex flex-column align-end" style="margin-top: auto; padding-top: 8px;">
                  <v-skeleton-loader type="text" width="30%" class="mb-1"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="50%"></v-skeleton-loader>
                </div>
              </div>
              <div v-else class="pa-4 d-flex flex-column" style="height: 100%; position: relative; padding-bottom: 20px;">
                <!--Title and Icon Row-->
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="text-uppercase font-weight-bold" style="font-size: 16px;">{{$t('homeStats.players')}}</span>
                  <v-icon data-testid="home-icon-players" :color="$vuetify.theme.dark ? 'white' : 'black'" size="28">directions_run</v-icon>
                </div>
                <!--Main Value-->
                <div class="mb-4" style="flex-grow: 1;">
                  <span data-testid="home-value-players" class="font-weight-bold" style="font-size: 28px !important; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; text-transform: none; letter-spacing: normal; line-height: 1.2;">{{clientPlayers}}</span>
                </div>
                <!--Trend and Total Section-->
                <div class="d-flex justify-end align-start" style="margin-top: auto; padding-top: 8px;">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center" style="margin-bottom: 2px;">
                      <v-icon data-testid="home-total-icon-players" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey'" size="16" class="mr-1">layers</v-icon>
                      <span data-testid="home-total-players" class="body-2 font-weight-medium">{{driblabStats.dP}}</span>
                    </div>
                    <span class="body-2 grey--text text--darken-1">{{$t('homeStats.totalPlayers')}}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <!--Teams Card-->
          <div style="flex: 1 1 0; min-width: 0;">
            <v-card data-testid="home-card-teams" elevation="0" outlined class="stats-card d-flex flex-column" style="border-radius: 8px; height: 100%;">
              <div v-if="loading || !driblabStats" class="pa-4 d-flex flex-column" style="height: 100%;">
                <div class="d-flex justify-space-between align-start mb-4">
                  <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                  <v-skeleton-loader type="avatar" size="28" class="ma-0"></v-skeleton-loader>
                </div>
                <div class="mb-4" style="flex-grow: 1;">
                  <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
                </div>
                <div class="d-flex flex-column align-end" style="margin-top: auto; padding-top: 8px;">
                  <v-skeleton-loader type="text" width="30%" class="mb-1"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="50%"></v-skeleton-loader>
                </div>
              </div>
              <div v-else class="pa-4 d-flex flex-column" style="height: 100%; position: relative; padding-bottom: 20px;">
                <!--Title and Icon Row-->
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="text-uppercase font-weight-bold" style="font-size: 16px;">{{$t('homeStats.teams')}}</span>
                  <v-icon data-testid="home-icon-teams" :color="$vuetify.theme.dark ? 'white' : 'black'" size="28">shield</v-icon>
                </div>
                <!--Main Value-->
                <div class="mb-4" style="flex-grow: 1;">
                  <span data-testid="home-value-teams" class="font-weight-bold" style="font-size: 28px !important; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; text-transform: none; letter-spacing: normal; line-height: 1.2;">{{clientTeams}}</span>
                </div>
                <!--Trend and Total Section-->
                <div class="d-flex justify-end align-start" style="margin-top: auto; padding-top: 8px;">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center" style="margin-bottom: 2px;">
                      <v-icon data-testid="home-total-icon-teams" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey'" size="16" class="mr-1">layers</v-icon>
                      <span data-testid="home-total-teams" class="body-2 font-weight-medium">{{driblabStats.dT}}</span>
                    </div>
                    <span class="body-2 grey--text text--darken-1">{{$t('homeStats.totalTeams')}}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <!--Leagues Card-->
          <div style="flex: 1 1 0; min-width: 0;">
            <v-card data-testid="home-card-leagues" elevation="0" outlined class="stats-card d-flex flex-column" style="border-radius: 8px; height: 100%;">
              <div v-if="loading || !driblabStats" class="pa-4 d-flex flex-column" style="height: 100%;">
                <div class="d-flex justify-space-between align-start mb-4">
                  <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                  <v-skeleton-loader type="avatar" size="28" class="ma-0"></v-skeleton-loader>
                </div>
                <div class="mb-4" style="flex-grow: 1;">
                  <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
                </div>
                <div class="d-flex flex-column align-end" style="margin-top: auto; padding-top: 8px;">
                  <v-skeleton-loader type="text" width="30%" class="mb-1"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="50%"></v-skeleton-loader>
                </div>
              </div>
              <div v-else class="pa-4 d-flex flex-column" style="height: 100%; position: relative; padding-bottom: 20px;">
                <!--Title and Icon Row-->
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="text-uppercase font-weight-bold" style="font-size: 16px;">{{$t('homeStats.leagues')}}</span>
                  <v-icon data-testid="home-icon-leagues" :color="$vuetify.theme.dark ? 'white' : 'black'" size="28">emoji_events</v-icon>
                </div>
                <!--Main Value-->
                <div class="mb-4" style="flex-grow: 1;">
                  <span data-testid="home-value-leagues" class="font-weight-bold" style="font-size: 28px !important; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; text-transform: none; letter-spacing: normal; line-height: 1.2;">{{clientLeagues}}</span>
                </div>
                <!--Trend and Total Section-->
                <div class="d-flex justify-end align-start" style="margin-top: auto; padding-top: 8px;">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center" style="margin-bottom: 2px;">
                      <v-icon data-testid="home-total-icon-leagues" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey'" size="16" class="mr-1">layers</v-icon>
                      <span data-testid="home-total-leagues" class="body-2 font-weight-medium">{{driblabStats.dL}}</span>
                    </div>
                    <span class="body-2 grey--text text--darken-1">{{$t('homeStats.totalLeagues')}}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <!--Seasons Card-->
          <div style="flex: 1 1 0; min-width: 0;">
            <v-card data-testid="home-card-seasons" elevation="0" outlined class="stats-card d-flex flex-column" style="border-radius: 8px; height: 100%;">
              <div v-if="loading || !driblabStats" class="pa-4 d-flex flex-column" style="height: 100%;">
                <div class="d-flex justify-space-between align-start mb-4">
                  <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                  <v-skeleton-loader type="avatar" size="28" class="ma-0"></v-skeleton-loader>
                </div>
                <div class="mb-4" style="flex-grow: 1;">
                  <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
                </div>
                <div class="d-flex flex-column align-end" style="margin-top: auto; padding-top: 8px;">
                  <v-skeleton-loader type="text" width="30%" class="mb-1"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="50%"></v-skeleton-loader>
                </div>
              </div>
              <div v-else class="pa-4 d-flex flex-column" style="height: 100%; position: relative; padding-bottom: 20px;">
                <!--Title and Icon Row-->
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="text-uppercase font-weight-bold" style="font-size: 16px;">{{$t('homeStats.seasons')}}</span>
                  <v-icon data-testid="home-icon-seasons" :color="$vuetify.theme.dark ? 'white' : 'black'" size="28">event</v-icon>
                </div>
                <!--Main Value-->
                <div class="mb-4" style="flex-grow: 1;">
                  <span data-testid="home-value-seasons" class="font-weight-bold" style="font-size: 28px !important; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; text-transform: none; letter-spacing: normal; line-height: 1.2;">{{clientSeasons}}</span>
                </div>
                <!--Trend and Total Section-->
                <div class="d-flex justify-end align-start" style="margin-top: auto; padding-top: 8px;">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center" style="margin-bottom: 2px;">
                      <v-icon data-testid="home-total-icon-seasons" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey'" size="16" class="mr-1">layers</v-icon>
                      <span data-testid="home-total-seasons" class="body-2 font-weight-medium">{{driblabStats.dS}}</span>
                    </div>
                    <span class="body-2 grey--text text--darken-1">{{$t('homeStats.totalSeasons')}}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <!--Games Card-->
          <div style="flex: 1 1 0; min-width: 0;">
            <v-card data-testid="home-card-games" elevation="0" outlined class="stats-card d-flex flex-column" style="border-radius: 8px; height: 100%;">
              <div v-if="loading || !driblabStats" class="pa-4 d-flex flex-column" style="height: 100%;">
                <div class="d-flex justify-space-between align-start mb-4">
                  <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                  <v-skeleton-loader type="avatar" size="28" class="ma-0"></v-skeleton-loader>
                </div>
                <div class="mb-4" style="flex-grow: 1;">
                  <v-skeleton-loader type="heading" width="60%"></v-skeleton-loader>
                </div>
                <div class="d-flex flex-column align-end" style="margin-top: auto; padding-top: 8px;">
                  <v-skeleton-loader type="text" width="30%" class="mb-1"></v-skeleton-loader>
                  <v-skeleton-loader type="text" width="50%"></v-skeleton-loader>
                </div>
              </div>
              <div v-else class="pa-4 d-flex flex-column" style="height: 100%; position: relative; padding-bottom: 20px;">
                <!--Title and Icon Row-->
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="text-uppercase font-weight-bold" style="font-size: 16px;">{{$t('homeStats.games')}}</span>
                  <v-icon data-testid="home-icon-games" :color="$vuetify.theme.dark ? 'white' : 'black'" size="28">sports_soccer</v-icon>
                </div>
                <!--Main Value-->
                <div class="mb-4" style="flex-grow: 1;">
                  <span data-testid="home-value-games" class="font-weight-bold" style="font-size: 28px !important; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; text-transform: none; letter-spacing: normal; line-height: 1.2;">{{clientGames}}</span>
                </div>
                <!--Trend and Total Section-->
                <div class="d-flex justify-end align-start" style="margin-top: auto; padding-top: 8px;">
                  <div class="d-flex flex-column align-end">
                    <div class="d-flex align-center" style="margin-bottom: 2px;">
                      <v-icon data-testid="home-total-icon-games" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey'" size="16" class="mr-1">layers</v-icon>
                      <span data-testid="home-total-games" class="body-2 font-weight-medium">{{driblabStats.dG}}</span>
                    </div>
                    <span class="body-2 grey--text text--darken-1">{{$t('homeStats.totalGames')}}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </div>

        <!--Stats List - Mobile/Tablet Layout-->
        <v-card v-if="$vuetify.breakpoint.smAndDown" elevation="1" class="mb-0" style="border-radius: 8px;">
          <v-card-text v-if="loading || !driblabStats" class="pa-3">
            <v-skeleton-loader type="list-item-avatar-three-line" class="mb-2"></v-skeleton-loader>
            <v-skeleton-loader type="list-item-avatar-three-line" class="mb-2"></v-skeleton-loader>
            <v-skeleton-loader type="list-item-avatar-three-line"></v-skeleton-loader>
          </v-card-text>

          <v-card-text v-else class="pa-0">
            <!--Players Item-->
            <div data-testid="home-mobile-players" class="d-flex align-center pa-3" style="border-bottom: 1px solid rgba(0,0,0,0.12);">
              <div class="d-flex align-center justify-center mr-3" style="width: 48px; height: 48px; border-radius: 4px;">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'black'" size="24">directions_run</v-icon>
              </div>
              <div class="d-flex flex-column flex-grow-1">
                <div class="d-flex align-baseline">
                  <span data-testid="home-mobile-value-players" class="font-weight-bold body-1 mr-1 primary--text" style="font-size: 18px; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; opacity: 1 !important;">{{clientPlayers}}</span>
                  <span class="body-2 grey--text text--darken-1">{{$t('homeStats.players').toLowerCase()}}</span>
                </div>
                <span class="body-2 grey--text text--darken-1 mt-1">{{driblabStats.dP}} total {{$t('homeStats.players').toLowerCase()}}</span>
              </div>
            </div>
            
            <!--Teams Item-->
            <div data-testid="home-mobile-teams" class="d-flex align-center pa-3" style="border-bottom: 1px solid rgba(0,0,0,0.12);">
              <div class="d-flex align-center justify-center mr-3" style="width: 48px; height: 48px; border-radius: 4px;">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'black'" size="24">shield</v-icon>
              </div>
              <div class="d-flex flex-column flex-grow-1">
                <div class="d-flex align-baseline">
                  <span data-testid="home-mobile-value-teams" class="font-weight-bold body-1 mr-1 primary--text" style="font-size: 18px; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; opacity: 1 !important;">{{clientTeams}}</span>
                  <span class="body-2 grey--text text--darken-1">{{$t('homeStats.teams').toLowerCase()}}</span>
                </div>
                <span class="body-2 grey--text text--darken-1 mt-1">{{driblabStats.dT}} total {{$t('homeStats.teams').toLowerCase()}}</span>
              </div>
            </div>
            
            <!--Leagues Item-->
            <div data-testid="home-mobile-leagues" class="d-flex align-center pa-3" style="border-bottom: 1px solid rgba(0,0,0,0.12);">
              <div class="d-flex align-center justify-center mr-3" style="width: 48px; height: 48px; border-radius: 4px;">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'black'" size="24">emoji_events</v-icon>
              </div>
              <div class="d-flex flex-column flex-grow-1">
                <div class="d-flex align-baseline">
                  <span data-testid="home-mobile-value-leagues" class="font-weight-bold body-1 mr-1 primary--text" style="font-size: 18px; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; opacity: 1 !important;">{{clientLeagues}}</span>
                  <span class="body-2 grey--text text--darken-1">{{$t('homeStats.leagues').toLowerCase()}}</span>
                </div>
                <span class="body-2 grey--text text--darken-1 mt-1">{{driblabStats.dL}} total {{$t('homeStats.leagues').toLowerCase()}}</span>
              </div>
            </div>
            
            <!--Seasons Item-->
            <div data-testid="home-mobile-seasons" class="d-flex align-center pa-3" style="border-bottom: 1px solid rgba(0,0,0,0.12);">
              <div class="d-flex align-center justify-center mr-3" style="width: 48px; height: 48px; border-radius: 4px;">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'black'" size="24">event</v-icon>
              </div>
              <div class="d-flex flex-column flex-grow-1">
                <div class="d-flex align-baseline">
                  <span data-testid="home-mobile-value-seasons" class="font-weight-bold body-1 mr-1 primary--text" style="font-size: 18px; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; opacity: 1 !important;">{{clientSeasons}}</span>
                  <span class="body-2 grey--text text--darken-1">{{$t('homeStats.seasons').toLowerCase()}}</span>
                </div>
                <span class="body-2 grey--text text--darken-1 mt-1">{{driblabStats.dS}} total {{$t('homeStats.seasons').toLowerCase()}}</span>
              </div>
            </div>
            
            <!--Games Item-->
            <div data-testid="home-mobile-games" class="d-flex align-center pa-3">
              <div class="d-flex align-center justify-center mr-3" style="width: 48px; height: 48px; border-radius: 4px;">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'black'" size="24">sports_soccer</v-icon>
              </div>
              <div class="d-flex flex-column flex-grow-1">
                <div class="d-flex align-baseline">
                  <span data-testid="home-mobile-value-games" class="font-weight-bold body-1 mr-1 primary--text" style="font-size: 18px; font-family: 'PPFormula Semi Extended Bold', sans-serif !important; opacity: 1 !important;">{{clientGames}}</span>
                  <span class="body-2 grey--text text--darken-1">{{$t('homeStats.games').toLowerCase()}}</span>
                </div>
                <span class="body-2 grey--text text--darken-1 mt-1">{{driblabStats.dG}} total {{$t('homeStats.games').toLowerCase()}}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" v-if="currentUser.clientType != 'Driblab' && currentUser.clientType != 'Media'">
        <ScoutDashboard v-if="currentUser.userType === 'Scout'"></ScoutDashboard>
        <CoachDashboard v-if="currentUser.userType === 'Coach'"></CoachDashboard>
        <OwnerDashboard v-if="currentUser.userType === 'Ownership'"></OwnerDashboard>
        <SportingDirectorDashboard v-if="currentUser.userType === 'SportingDirector'"></SportingDirectorDashboard>
        <IndividualDashboard v-if="currentUser.userType === 'Individual'"></IndividualDashboard>
        <BrandDashboard v-if="currentUser.userType === 'Brand'"></BrandDashboard>
        <AgencyDashboard v-if="currentUser.userType === 'Agency'"></AgencyDashboard>
        <FederationDashboard v-if="currentUser.userType === 'Federation'"></FederationDashboard>
      </v-col>
      <v-col cols="12" v-if="currentUser.clientType === 'Media'">
        <IndividualDashboard></IndividualDashboard>
      </v-col>
      <v-col cols="12" class="" v-if="currentUser.clientType === 'Driblab'" >
        <DriblabDashboard></DriblabDashboard>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AgencyDashboard from "./dashboard/AgencyDashboard";
import BrandDashboard from "./dashboard/BrandDashboard";
import CoachDashboard from "./dashboard/CoachDashboard";
import FederationDashboard from "./dashboard/FederationDashboard";
import IndividualDashboard from "./dashboard/IndividualDashboard";
import MediaDashboard from "./dashboard/MediaDashboard";
import OwnerDashboard from "./dashboard/OwnerDashboard";
import ScoutDashboard from "./dashboard/ScoutDashboard";
import SportingDirectorDashboard from "./dashboard/SportingDirectorDashboard";
import DriblabDashboard from './dashboard/DriblabDashboard';
import StatService from '../services/stat.service';

export default {
  name: "Home",
  components: {
    AgencyDashboard,
    BrandDashboard,
    CoachDashboard,
    FederationDashboard,
    IndividualDashboard,
    MediaDashboard,
    OwnerDashboard,
    ScoutDashboard,
    SportingDirectorDashboard,
    DriblabDashboard
  },
  title () {
    return this.$t('dashboards.title');
  },
  data() {
    return {
      overrideWindowTitle: true,
      content: "",
      loading: false,
      clientStats: null,
      userPlayers: null,
      driblabStats: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    clientPlayers() {
      //If client players is bigger than driblab total, return total
      if (this.userPlayers && this.driblabStats) return this.userPlayers > this.driblabStats.dP ? this.driblabStats.dP : this.userPlayers;
    },
    clientTeams() {
       //If client teams is bigger than driblab total, return total
      if (this.clientStats && this.driblabStats) return this.clientStats.cT > this.driblabStats.dT ? this.driblabStats.dT : this.clientStats.cT;
    },
    clientLeagues() {
       //If client leagues is bigger than driblab total, return total
      if (this.clientStats && this.driblabStats) return this.clientStats.cL > this.driblabStats.dL ? this.driblabStats.dL : this.clientStats.cL;
    },
    clientSeasons() {
       //If client seasons is bigger than driblab total, return total
      if (this.clientStats && this.driblabStats) return this.clientStats.cS > this.driblabStats.dS ? this.driblabStats.dS : this.clientStats.cS;
    },
    clientGames() {
       //If client games is bigger than driblab total, return total
      if (this.clientStats && this.driblabStats) return this.clientStats.cG > this.driblabStats.dG ? this.driblabStats.dG : this.clientStats.cG;
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    isRtl(){
      return this.currentUserLanguage === 'sa'
    },
    numOfCols() {
      if (this.$vuetify.breakpoint.xsOnly) return 12;
      else if (this.$vuetify.breakpoint.mdAndDown) return 3;
      else return 2;
    }
  },
  mounted() {
    this.getHistoryStats();
    this.getClientPlayers();
    this.getOverallStats();
  },
  methods: {
    async getHistoryStats() {
      this.loading = true;
      const response = await StatService.getHistoryStats();
      this.driblabStats = response.data;
      this.loading = false;
    },
    async getClientPlayers() {
      const response = await StatService.getClientOverallPlayersStats(this.currentUser.clientId);
      this.userPlayers = response.data;
    },
    async getOverallStats() {
      const response = await StatService.getClientOverallStats(this.currentUser.clientId);
      this.clientStats = response.data;
    }
  }
};
</script>
